import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useContractServices } from '../../context/ContractServicesContext';
import {
  Play,
  Check,
  PlayCircle,
  Clock,
  BarChart4,
  Sliders,
  Info,
  AlertCircle
} from 'lucide-react';
import skyLoader from "../../assets/home-assets/skyLoader.gif";

const EmissionsAdminPanel = ({ adminService, refreshKey = 0, onTransactionSubmit = () => { } }) => {
  const { services, isReady } = useContractServices();

  const [epochInfo, setEpochInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    startEpoch: false,
    processEpoch: false,
    forceStopEpoch: false,
  });

  // Current contract values
  const [currentEpochDuration, setCurrentEpochDuration] = useState(null);
  const [currentBaseEmissions, setCurrentBaseEmissions] = useState(null);
  const [currentMaxEmissions, setCurrentMaxEmissions] = useState(null);

  // Form states
  const [globalMetricsScore, setGlobalMetricsScore] = useState('');
  const [projectMetricsForm, setProjectMetricsForm] = useState({
    projectId: '',
    score: ''
  });
  const [epochDuration, setEpochDuration] = useState('');
  const [baseEmissions, setBaseEmissions] = useState('');
  const [maxEmissions, setMaxEmissions] = useState('');
  const [weights, setWeights] = useState({
    staking: '',
    metrics: ''
  });
  const [shares, setShares] = useState({
    staking: '',
    iuHolders: ''
  });

  // Fetch contract data
  useEffect(() => {
    fetchAllContractData();
  }, [isReady, services, refreshKey]);

  // Fetch all contract data
  const fetchAllContractData = async () => {
    if (!isReady) return;

    try {
      setLoading(true);

      // Fetch epoch info
      const info = await services.emissionController.getCurrentEpochInfo();
      setEpochInfo(info);

      // Fetch epoch duration
      const durationInDays = await services.emissionController.getEpochDurationInDays();
      setCurrentEpochDuration(durationInDays);

      // Fetch base emissions
      const baseEmissions = await services.emissionController.getBaseEmissionsPerEpoch();
      setCurrentBaseEmissions(baseEmissions.formatted);

      // Fetch max emissions
      const maxEmissions = await services.emissionController.getMaxEmissionsPerEpoch();
      setCurrentMaxEmissions(maxEmissions.formatted);
    } catch (error) {
      console.error('Error fetching contract data:', error);
      toast.error('Failed to fetch contract information');
    } finally {
      setLoading(false);
    }
  };

  // Start a new epoch
  const handleStartEpoch = async () => {
    try {
      setActionLoading(prev => ({ ...prev, startEpoch: true }));
      await adminService.startEpoch();
      toast.success('New epoch started successfully');

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();
    } catch (error) {
      console.error('Error starting epoch:', error);
      toast.error('Failed to start new epoch');
    } finally {
      setActionLoading(prev => ({ ...prev, startEpoch: false }));
    }
  };

  // Process the current epoch
  const handleProcessEpoch = async () => {
    try {
      setActionLoading(prev => ({ ...prev, processEpoch: true }));
      await adminService.processEpoch();
      toast.success('Epoch processed successfully');

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();
    } catch (error) {
      console.error('Error processing epoch:', error);
      toast.error('Failed to process epoch');
    } finally {
      setActionLoading(prev => ({ ...prev, processEpoch: false }));
    }
  };

  // Force stop the current epoch (for debugging)
  const handleForceStopEpoch = async () => {
    try {
      setActionLoading(prev => ({ ...prev, forceStopEpoch: true }));
      await adminService.forceStopEpoch();
      toast.success('Epoch force stopped for debugging. You can now process it.');

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();
    } catch (error) {
      console.error('Error force stopping epoch:', error);
      toast.error('Failed to force stop epoch');
    } finally {
      setActionLoading(prev => ({ ...prev, forceStopEpoch: false }));
    }
  };

  // Set global metrics score
  const handleSetGlobalMetricsScore = async (e) => {
    e.preventDefault();
    if (!globalMetricsScore) return;

    try {
      setLoading(true);
      await adminService.setGlobalMetricsScore(globalMetricsScore);
      toast.success(`Global metrics score set to ${globalMetricsScore}`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setGlobalMetricsScore('');
    } catch (error) {
      console.error('Error setting global metrics score:', error);
      toast.error('Failed to set global metrics score');
    } finally {
      setLoading(false);
    }
  };

  // Set project metrics score
  const handleSetProjectMetricsScore = async (e) => {
    e.preventDefault();
    if (!projectMetricsForm.projectId || !projectMetricsForm.score) return;

    try {
      setLoading(true);
      await adminService.setProjectMetricsScore(
        projectMetricsForm.projectId,
        projectMetricsForm.score
      );
      toast.success(`Metrics score for project #${projectMetricsForm.projectId} set to ${projectMetricsForm.score}`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setProjectMetricsForm({ projectId: '', score: '' });
    } catch (error) {
      console.error('Error setting project metrics score:', error);
      toast.error('Failed to set project metrics score');
    } finally {
      setLoading(false);
    }
  };

  // Set epoch duration
  const handleSetEpochDuration = async (e) => {
    e.preventDefault();
    if (!epochDuration) return;

    try {
      setLoading(true);
      await adminService.setEpochDuration(epochDuration);
      toast.success(`Epoch duration set to ${epochDuration} days`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setEpochDuration('');
    } catch (error) {
      console.error('Error setting epoch duration:', error);
      toast.error('Failed to set epoch duration');
    } finally {
      setLoading(false);
    }
  };

  // Set base emissions
  const handleSetBaseEmissions = async (e) => {
    e.preventDefault();
    if (!baseEmissions) return;

    try {
      setLoading(true);
      await adminService.setBaseEmissionsPerEpoch(baseEmissions);
      toast.success(`Base emissions set to ${baseEmissions} CEL`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setBaseEmissions('');
    } catch (error) {
      console.error('Error setting base emissions:', error);
      toast.error('Failed to set base emissions');
    } finally {
      setLoading(false);
    }
  };

  // Set max emissions
  const handleSetMaxEmissions = async (e) => {
    e.preventDefault();
    if (!maxEmissions) return;

    try {
      setLoading(true);
      await adminService.setMaxEmissionsPerEpoch(maxEmissions);
      toast.success(`Max emissions set to ${maxEmissions} CEL`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setMaxEmissions('');
    } catch (error) {
      console.error('Error setting max emissions:', error);
      toast.error('Failed to set max emissions');
    } finally {
      setLoading(false);
    }
  };

  // Set weights
  const handleSetWeights = async (e) => {
    e.preventDefault();
    if (!weights.staking || !weights.metrics) return;

    // Check that weights sum to 100
    if (parseInt(weights.staking) + parseInt(weights.metrics) !== 100) {
      toast.error('Weights must sum to 100%');
      return;
    }

    try {
      setLoading(true);
      await adminService.setGlobalWeights(
        parseInt(weights.staking),
        parseInt(weights.metrics)
      );
      toast.success(`Weights set to: Staking ${weights.staking}%, Metrics ${weights.metrics}%`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setWeights({ staking: '', metrics: '' });
    } catch (error) {
      console.error('Error setting weights:', error);
      toast.error('Failed to set weights');
    } finally {
      setLoading(false);
    }
  };

  // Set shares
  const handleSetShares = async (e) => {
    e.preventDefault();
    if (!shares.staking || !shares.iuHolders) return;

    // Check that shares sum to 100
    if (parseInt(shares.staking) + parseInt(shares.iuHolders) !== 100) {
      toast.error('Shares must sum to 100%');
      return;
    }

    try {
      setLoading(true);
      await adminService.setEmissionShares(
        parseInt(shares.staking),
        parseInt(shares.iuHolders)
      );
      toast.success(`Shares set to: Staking ${shares.staking}%, IU Holders ${shares.iuHolders}%`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Refresh all data
      await fetchAllContractData();

      setShares({ staking: '', iuHolders: '' });
    } catch (error) {
      console.error('Error setting shares:', error);
      toast.error('Failed to set shares');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Emissions Management</h2>

      {/* Current Epoch Status */}
      <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
          <Clock className="h-5 w-5 text-indigo-500 mr-2" />
          Current Epoch Status
        </h3>

        {loading ? (
          <div className="flex justify-center">
            <div className="flex justify-center items-center h-100">
              <img
                src={skyLoader}
                alt="Loading..."
                className="w-100 h-100 object-contain"
              />
            </div>          </div>
        ) : epochInfo ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Epoch Number:</p>
              <p className="font-medium">{epochInfo.currentEpochNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status:</p>
              <p className={`font-medium flex items-center ${epochInfo.isActive ? 'text-green-600' : 'text-red-600'}`}>
                {epochInfo.isActive ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Active
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Inactive
                  </>
                )}
              </p>
            </div>

            {epochInfo.isActive && (
              <>
                <div>
                  <p className="text-sm text-gray-600">Start Time:</p>
                  <p className="font-medium">
                    {new Date(epochInfo.startTime * 1000).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">End Time:</p>
                  <p className="font-medium">
                    {new Date(epochInfo.endTime * 1000).toLocaleString()}
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <p className="text-gray-500">Failed to load epoch information</p>
        )}

        <div className="flex gap-4 mt-6">
          <button
            onClick={handleStartEpoch}
            disabled={loading || actionLoading.startEpoch || (epochInfo && epochInfo.isActive)}
            className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {actionLoading.startEpoch ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <PlayCircle className="h-4 w-4 mr-2" />
            )}
            Start New Epoch
          </button>

          <button
            onClick={handleProcessEpoch}
            disabled={loading || actionLoading.processEpoch || !(epochInfo && epochInfo.isActive)}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {actionLoading.processEpoch ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Process Current Epoch
          </button>

          <button
            onClick={handleForceStopEpoch}
            disabled={loading || actionLoading.forceStopEpoch || !(epochInfo && epochInfo.isActive)}
            className="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            title="Debug: Force stops the current epoch to allow immediate processing (sets end timestamp to now)"
          >
            {actionLoading.forceStopEpoch ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <AlertCircle className="h-4 w-4 mr-2" />
            )}
            Debug: Force Stop Epoch
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Metrics Configuration */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <BarChart4 className="h-5 w-5 text-indigo-500 mr-2" />
            Metrics Configuration
          </h3>

          {/* Global Metrics Score */}
          <form onSubmit={handleSetGlobalMetricsScore} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Global Metrics Score
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={globalMetricsScore}
                onChange={(e) => setGlobalMetricsScore(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="Enter global score"
                min="0"
              />
              <button
                type="submit"
                disabled={loading || !globalMetricsScore}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
          </form>

          {/* Project Metrics Score */}
          <form onSubmit={handleSetProjectMetricsScore}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Metrics Score
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="number"
                value={projectMetricsForm.projectId}
                onChange={(e) => setProjectMetricsForm(prev => ({ ...prev, projectId: e.target.value }))}
                className="p-2 border border-gray-300 rounded-md"
                placeholder="Project ID"
                min="0"
              />
              <input
                type="number"
                value={projectMetricsForm.score}
                onChange={(e) => setProjectMetricsForm(prev => ({ ...prev, score: e.target.value }))}
                className="p-2 border border-gray-300 rounded-md"
                placeholder="Score"
                min="0"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !projectMetricsForm.projectId || !projectMetricsForm.score}
              className="w-full px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Project Score
            </button>
          </form>
        </div>

        {/* Epoch Configuration */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <Clock className="h-5 w-5 text-indigo-500 mr-2" />
            Epoch Configuration
          </h3>

          {/* Epoch Duration */}
          <form onSubmit={handleSetEpochDuration} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Epoch Duration (days)
            </label>
            {currentEpochDuration !== null && (
              <div className="bg-gray-50 p-2 rounded-md mb-2 flex items-center">
                <span className="text-xs font-medium text-gray-500 mr-2">Current Value:</span>
                <span className="text-sm font-medium text-gray-700">{currentEpochDuration} days</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="number"
                value={epochDuration}
                onChange={(e) => setEpochDuration(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 30"
                min="1"
                max="90"
              />
              <button
                type="submit"
                disabled={loading || !epochDuration}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Duration must be between 1 and 90 days
            </p>
          </form>

          {/* Base Emissions */}
          <form onSubmit={handleSetBaseEmissions} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Emissions per Epoch (CEL)
            </label>
            {currentBaseEmissions !== null && (
              <div className="bg-gray-50 p-2 rounded-md mb-2 flex items-center">
                <span className="text-xs font-medium text-gray-500 mr-2">Current Value:</span>
                <span className="text-sm font-medium text-gray-700">{currentBaseEmissions} CEL</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="number"
                value={baseEmissions}
                onChange={(e) => setBaseEmissions(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 10000"
                min="1"
              />
              <button
                type="submit"
                disabled={loading || !baseEmissions}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
          </form>

          {/* Max Emissions */}
          <form onSubmit={handleSetMaxEmissions}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum Emissions per Epoch (CEL)
            </label>
            {currentMaxEmissions !== null && (
              <div className="bg-gray-50 p-2 rounded-md mb-2 flex items-center">
                <span className="text-xs font-medium text-gray-500 mr-2">Current Value:</span>
                <span className="text-sm font-medium text-gray-700">{currentMaxEmissions} CEL</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="number"
                value={maxEmissions}
                onChange={(e) => setMaxEmissions(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 100000"
                min="1"
              />
              <button
                type="submit"
                disabled={loading || !maxEmissions}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Must be greater than or equal to base emissions
            </p>
          </form>
        </div>

        {/* Distribution Configuration */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <Sliders className="h-5 w-5 text-indigo-500 mr-2" />
            Distribution Configuration
          </h3>

          {/* Weights */}
          <form onSubmit={handleSetWeights} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Global Weights (%)
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Staking Score
                </label>
                <input
                  type="number"
                  value={weights.staking}
                  onChange={(e) => setWeights(prev => ({ ...prev, staking: e.target.value }))}
                  className="p-2 border border-gray-300 rounded-md w-full"
                  placeholder="e.g., 50"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Metrics Score
                </label>
                <input
                  type="number"
                  value={weights.metrics}
                  onChange={(e) => setWeights(prev => ({ ...prev, metrics: e.target.value }))}
                  className="p-2 border border-gray-300 rounded-md w-full"
                  placeholder="e.g., 50"
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || !weights.staking || !weights.metrics}
              className="w-full px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Weights
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Weights must sum to exactly 100%
            </p>
          </form>

          {/* Shares */}
          <form onSubmit={handleSetShares}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emission Shares (%)
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Staking Share
                </label>
                <input
                  type="number"
                  value={shares.staking}
                  onChange={(e) => setShares(prev => ({ ...prev, staking: e.target.value }))}
                  className="p-2 border border-gray-300 rounded-md w-full"
                  placeholder="e.g., 20"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  IU Holders Share
                </label>
                <input
                  type="number"
                  value={shares.iuHolders}
                  onChange={(e) => setShares(prev => ({ ...prev, iuHolders: e.target.value }))}
                  className="p-2 border border-gray-300 rounded-md w-full"
                  placeholder="e.g., 80"
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || !shares.staking || !shares.iuHolders}
              className="w-full px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Shares
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Shares must sum to exactly 100%
            </p>
          </form>
        </div>

        {/* Emergency Functions */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-red-700 mb-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            Emergency Functions
          </h3>

          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
              <p className="text-sm text-red-600">
                These functions should only be used in emergency situations. Please use with extreme caution.
              </p>
            </div>
          </div>

          {/* Emergency Withdraw */}
          <form>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emergency Withdraw
            </label>
            <p className="text-xs text-gray-500 mb-4">
              Contact the development team for assistance with emergency withdrawals.
            </p>
            <button
              type="button"
              disabled={true}
              className="w-full px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Emergency Withdraw (Disabled)
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmissionsAdminPanel; 